import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import Table from "sap/m/Table";
import SearchField, { SearchField$SearchEvent } from "sap/m/SearchField";
import { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Sorter from "sap/ui/model/Sorter";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import Control from "sap/ui/core/Control";
import SmarterTable from "ips/ac/syscon/SmarterTable";
import Util from "ips/ac/util/Util";

export interface ListHandlerSettings {
  entitySet: string;
  detailRoute: string;
  tableId?: string;
  sorter?: Sorter;
  filters?: Filter[];
  additionalColumns?: object[];
  tableHeaderKey?: string;
  parameters?: object;
}

/**
 * @namespace ips.ac.base.handler
 */
export default class ListHandler {

  private readonly controller: BaseTypedController;
  private readonly settings: ListHandlerSettings;
  private readonly table: Table;
  private smarterTableInitialized = false;

  public constructor(controller: BaseTypedController, settings: ListHandlerSettings) {
    this.controller = controller;
    this.settings = settings;
    this.table = controller.byId(settings.tableId ?? "table") as Table;
  }

  public getTable(): Table {
    return this.table;
  }

  public init(): void {
    this.bindTable();
    this.initSmarterTable();
    this.attachRouteMatched();
  }

  public onFilterTable(oEvent: SearchField$SearchEvent): void {
    const searchFilter = this.getFilterFromSearchField(oEvent.getSource());
    const binding = this.table.getBinding("items") as ODataListBinding;
    const filters = [...(this.settings.filters ?? [])];

    if (searchFilter) {
      filters.push(searchFilter);
    }

    binding.filter(filters.length > 0 ? new Filter({ filters, and: true }) : []);
  }

  public onItemPress(oEvent: ListItemBase$PressEvent): void {
    const item = oEvent.getSource();
    const bindingContext = item.getBindingContext();

    this.controller.navTo(this.settings.detailRoute, {
      path: encodeURIComponent(bindingContext.getPath().substring(1)),
      guid: bindingContext.getProperty("Guid"),
    }, false);
  }

  private attachRouteMatched(): void {
    const router = Util.getComponent().getRouter();
    const routeInfo = router.getRouteInfoByHash(router.getHashChanger().getHash());

    if (!routeInfo) {
      return;
    }

    router.getRoute(routeInfo.name)?.attachMatched(() => this.bindTable(), this);
  }

  private bindTable(): void {
    if (this.table.isBound("items")) {
      (this.table.getBinding("items") as ODataListBinding).refresh();
      return;
    }

    const bindingInfo = this.table.getBindingInfo("items") as { template?: Control };
    const template = bindingInfo?.template ?? this.table.getItems()[0].clone();

    this.table.removeAllItems();
    this.table.bindItems({
      path: "/" + this.settings.entitySet,
      template: template,
      templateShareable: true,
      sorter: this.settings.sorter,
      filters: this.settings.filters,
      parameters: this.settings.parameters,
    });
  }

  private initSmarterTable(): void {
    if (this.smarterTableInitialized || !this.settings.additionalColumns) {
      return;
    }

    const title = Util.getText("@" + (this.settings.tableHeaderKey ?? this.settings.entitySet));

    new SmarterTable(this.controller, this.settings.tableId ?? "table", title, this.settings.additionalColumns);
    this.smarterTableInitialized = true;
  }

  private getFilterFromSearchField(searchField: SearchField): Filter | undefined {
    const value = searchField.getValue();

    if (!value) {
      return undefined;
    }

    const filterProperties = searchField.data("filterProperties") as { values: string[] };
    const properties = filterProperties?.values ?? [];
    const filters = properties.map((property) => new Filter({
      path: property,
      operator: FilterOperator.Contains,
      value1: value,
      caseSensitive: false,
    }));

    return filters.length > 0 ? new Filter({ filters, and: false }) : undefined;
  }
}
